import { FormEvent, useState } from 'react'

import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import { Spinner } from '@components/UI/Spinner'
import { v1 } from '@services/api'
import { useMutation } from 'react-query'
import { useRouter } from 'next/router'

export default function PasswordReset() {
  const { push, query } = useRouter()
  const [error, setError] = useState('')
  const resetPasswordMutation = useMutation(
    async (newPassword: object) =>
      await v1.put('/users/resetPassword', newPassword),
    {
      onSuccess: () => {
        void push('/login?ref=password-reset-success')
      }
    }
  )
  const sendEmailMutation = useMutation(
    async (email: string) => await v1.get(`/users/resetPassword?email=${email}`)
  )

  function sendEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const data = new FormData(e.currentTarget)
    const email = data.get('email') as string

    try {
      sendEmailMutation.mutate(email)
    } catch (error) {
      setError(error.message)
    }
  }

  function resetPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const data = new FormData(e.currentTarget)
    const password = data.get('reset-password') as string
    const confirmPassword = data.get('reset-password-confirm') as string

    if (password === confirmPassword) {
      try {
        resetPasswordMutation.mutate({
          reset_token: query.resetToken,
          user_password: password
        })
      } catch (error) {
        setError(error.message)
      }
    } else {
      setError('Your password confirmation must match your password')
    }
  }

  return (
    <>
      <NextSeo title="Reset Password" noindex={true} />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <img
            src="/images/product-pages/login-hero.png"
            className="md:order-first order-last md:mb-0 mb-8"
            width="50%"
          />

          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-last order-first">
            {!sendEmailMutation.isSuccess && (
              <h2 className="font-black tracking-tight mb-8 text-3xl text-center w-full">
                Reset Password
              </h2>
            )}
            {!query.resetToken && sendEmailMutation.isSuccess && (
              <div className="w-full">
                <h2 className="font-black tracking-tight mb-8 text-3xl text-center w-full">
                  Thank You!
                </h2>
                <p>
                  If the email you have entered exists in our system, we will
                  send you a link to reset your password in a few seconds. Be
                  sure to check your junk or spam folder if the email does not
                  show up soon.
                </p>
              </div>
            )}
            {!query.resetToken && !sendEmailMutation.isSuccess && (
              <form onSubmit={sendEmail} className="w-full">
                <section className="mb-4">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    name="email"
                    type="email"
                    required
                    placeholder="example@example.com"
                  />
                </section>
                {error && (
                  <p className="mb-8 text-red-400 text-center">{error}</p>
                )}
                <section className="flex justify-start">
                  {sendEmailMutation.isLoading ? (
                    <Spinner />
                  ) : (
                    <Button variant="secondary" clipSide="right" wide>
                      Submit
                    </Button>
                  )}
                </section>
              </form>
            )}

            {query?.resetToken && (
              <form onSubmit={resetPassword} className="w-full">
                <section className="mb-4">
                  <Label htmlFor="reset-password">New Password</Label>
                  <Input
                    id="reset-password"
                    name="reset-password"
                    type="password"
                    className="mb-4"
                    required
                  />

                  <Label htmlFor="reset-password-confirm">
                    Confirm New Password
                  </Label>
                  <Input
                    id="reset-password-confirm"
                    name="reset-password-confirm"
                    type="password"
                    required
                  />
                </section>
                {error && (
                  <p className="mb-8 text-red-400 text-center">{error}</p>
                )}
                <section className="flex justify-start">
                  {sendEmailMutation.isLoading ? (
                    <Spinner />
                  ) : (
                    <Button variant="secondary" clipSide="right" wide>
                      Submit
                    </Button>
                  )}
                </section>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
